import { Fragment, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Radio } from 'lucide-react';
import type { RevenueInvoicesResponse } from '../types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const CATEGORY_COLORS: Record<string, string> = {
  Hosting: 'bg-blue-100 text-blue-800',
  Calibration: 'bg-green-100 text-green-800',
  LTE: 'bg-purple-100 text-purple-800',
  Installation: 'bg-orange-100 text-orange-800',
  Shipping: 'bg-gray-100 text-gray-800',
};

const SENSOR_STATUS_LABELS: Record<number, string> = {
  0: 'Unmanaged',
  1: 'Active',
  2: 'Too Low',
  3: 'Too High',
  4: 'To Acknowledge',
  5: 'Inactive',
  6: 'Maintenance',
  8: 'Inhibited',
  9: 'Archived',
};

interface RevenueInvoicesTableProps {
  data?: RevenueInvoicesResponse;
  loading: boolean;
  sortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  onPageChange: (page: number) => void;
}

function SortIcon({ column, sortKey, sortDirection }: { column: string; sortKey: string; sortDirection: 1 | -1 }) {
  if (column !== sortKey) return <ArrowUpDown className="ml-1 h-3 w-3" />;
  return sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
}

export function RevenueInvoicesTable({ data, loading, sortKey, sortDirection, onSort, onPageChange }: RevenueInvoicesTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageNumber = data?.page_number ?? 0;
  const pageLength = data?.page_length ?? 25;
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageLength);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Invoices</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('invoice_number')}>
                <span className="flex items-center">Invoice # <SortIcon column="invoice_number" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('account_name')}>
                <span className="flex items-center">Account <SortIcon column="account_name" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('invoice_date')}>
                <span className="flex items-center">Date <SortIcon column="invoice_date" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('month')}>
                <span className="flex items-center">Month <SortIcon column="month" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead>Categories</TableHead>
              <TableHead className="text-center">Sensors</TableHead>
              <TableHead className="cursor-pointer select-none text-right" onClick={() => onSort('amount')}>
                <span className="flex items-center justify-end">Amount <SortIcon column="amount" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell />
                  <TableCell><div className="h-4 w-16 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-40 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-24 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-10 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-8 animate-pulse rounded bg-muted mx-auto" /></TableCell>
                  <TableCell className="text-right"><div className="ml-auto h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
                </TableRow>
              ))
            ) : data?.invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              data?.invoices.map((invoice) => {
                const isExpanded = expandedRows.has(invoice.id);
                const categories = [...new Set(invoice.lines.map((l) => l.category))];
                const hasDetails = invoice.lines.length > 0 || invoice.sensors.length > 0;

                return (
                  <Fragment key={invoice.id}>
                    <TableRow className={hasDetails ? 'cursor-pointer hover:bg-muted/50' : ''} onClick={() => hasDetails && toggleRow(invoice.id)}>
                      <TableCell className="w-8 px-2">
                        {hasDetails && (
                          isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.account_name}</TableCell>
                      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                      <TableCell>{invoice.month || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {categories.map((cat) => (
                            <Badge key={cat} variant="secondary" className={CATEGORY_COLORS[cat] || ''}>
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {invoice.sensor_count > 0 ? (
                          <Badge variant="outline" className="font-mono">
                            {invoice.sensor_count}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(invoice.total_amount)}</TableCell>
                    </TableRow>
                    {isExpanded && (
                      <>
                        {invoice.lines.map((line) => (
                          <TableRow key={line.id} className="bg-muted/30">
                            <TableCell />
                            <TableCell />
                            <TableCell colSpan={4} className="text-sm text-muted-foreground">
                              <Badge variant="outline" className="mr-2">{line.category}</Badge>
                              {line.sku}
                            </TableCell>
                            <TableCell />
                            <TableCell className="text-right text-sm">{formatCurrency(line.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {invoice.sensors.length > 0 && (
                          <TableRow className="bg-blue-50/50">
                            <TableCell />
                            <TableCell colSpan={7} className="py-2">
                              <div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                                <Radio className="h-3 w-3" /> Sensors ({invoice.sensors.length})
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {invoice.sensors.map((sensor) => (
                                  <div key={sensor.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                                    <span className="font-medium">{sensor.name}</span>
                                    {sensor.serial_number && (
                                      <span className="text-muted-foreground">({sensor.serial_number})</span>
                                    )}
                                    {sensor.status != null && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                                        {SENSOR_STATUS_LABELS[sensor.status] || `Status ${sensor.status}`}
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {pageNumber * pageLength + 1} to {Math.min((pageNumber + 1) * pageLength, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pageNumber === 0} onClick={() => onPageChange(pageNumber - 1)}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pageNumber + 1} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={pageNumber >= totalPages - 1} onClick={() => onPageChange(pageNumber + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
